import React, { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Environment, Html, OrbitControls, useGLTF } from "@react-three/drei";
import * as THREE from "three";

const REGION_CONFIG = {
  frontal: {
    key: "frontal",
    label: "Frontal Lobe",
    position: [0.6, 0.42, 0.18],
    lookAt: [0.3, 0.2, 0],
    camera: [1.8, 0.9, 2.5],
    color: "#ff5f6d",
    type: "surface",
    description: "Planning, reasoning, judgment, attention, and behavior.",
    why: "Often considered when cognition, attention, behavior, or executive function symptoms are discussed.",
  },
  temporal: {
    key: "temporal",
    label: "Temporal Lobe",
    position: [0.82, 0.04, 0.22],
    lookAt: [0.42, 0.04, 0.08],
    camera: [2.0, 0.35, 2.35],
    color: "#ff9f43",
    type: "surface",
    description: "Hearing, memory, language processing.",
    why: "Often relevant when memory, auditory, or language-related symptoms are discussed.",
  },
  parietal: {
    key: "parietal",
    label: "Parietal Lobe",
    position: [0.12, 0.52, -0.02],
    lookAt: [0.05, 0.3, 0],
    camera: [1.2, 1.3, 2.5],
    color: "#feca57",
    type: "surface",
    description: "Sensory integration, body awareness, spatial processing.",
    why: "Often considered in sensory interpretation or body-awareness issues.",
  },
  occipital: {
    key: "occipital",
    label: "Occipital Lobe",
    position: [-0.66, 0.15, -0.18],
    lookAt: [-0.35, 0.08, -0.06],
    camera: [-1.8, 0.4, 2.4],
    color: "#48dbfb",
    type: "surface",
    description: "Visual processing center.",
    why: "Relevant if visual symptoms or visual interpretation issues are discussed.",
  },
  hippocampus: {
    key: "hippocampus",
    label: "Hippocampus",
    position: [0.18, -0.02, 0.12],
    lookAt: [0.1, -0.02, 0.06],
    camera: [1.3, 0.15, 2.0],
    color: "#6c5ce7",
    type: "deep",
    description: "Memory formation and recall.",
    why: "Often linked with memory-related symptoms.",
  },
  amygdala: {
    key: "amygdala",
    label: "Amygdala",
    position: [0.26, -0.09, 0.18],
    lookAt: [0.14, -0.06, 0.08],
    camera: [1.35, 0.08, 2.0],
    color: "#ee5253",
    type: "deep",
    description: "Emotion, fear, stress response.",
    why: "Often referenced in anxiety, emotional regulation, or stress-related patterns.",
  },
  motor: {
    key: "motor",
    label: "Motor Cortex",
    position: [0.28, 0.34, 0.08],
    lookAt: [0.14, 0.22, 0.02],
    camera: [1.4, 0.85, 2.2],
    color: "#10ac84",
    type: "surface",
    description: "Movement initiation and control.",
    why: "Often considered when weakness, movement issues, or motor symptoms are discussed.",
  },
  basal_ganglia: {
    key: "basal_ganglia",
    label: "Basal Ganglia",
    position: [0.02, -0.05, -0.04],
    lookAt: [0.0, -0.02, 0.0],
    camera: [1.1, 0.18, 1.95],
    color: "#341f97",
    type: "deep",
    description: "Movement regulation and motor coordination loops.",
    why: "Often relevant when movement regulation, stiffness, or motor pathway issues are discussed.",
  },
  brainstem: {
    key: "brainstem",
    label: "Brainstem",
    position: [-0.04, -0.64, -0.02],
    lookAt: [0.0, -0.46, 0.0],
    camera: [0.85, -0.28, 2.0],
    color: "#00d2d3",
    type: "deep",
    description: "Autonomic control, relay center, vital pathways.",
    why: "Often considered in balance, autonomic, cranial nerve, or central relay concerns.",
  },
  cerebellum: {
    key: "cerebellum",
    label: "Cerebellum",
    position: [-0.38, -0.34, -0.28],
    lookAt: [-0.22, -0.22, -0.16],
    camera: [-1.45, -0.12, 2.15],
    color: "#54a0ff",
    type: "surface",
    description: "Balance, coordination, posture.",
    why: "Often relevant when vertigo, imbalance, or coordination issues are discussed.",
  },
  spinal_cord: {
    key: "spinal_cord",
    label: "Spinal Cord",
    position: [-0.02, -0.94, -0.04],
    lookAt: [0.0, -0.72, -0.02],
    camera: [0.75, -0.6, 2.0],
    color: "#1dd1a1",
    type: "deep",
    description: "Signal pathway from brain to body.",
    why: "Relevant when conduction, limb symptoms, or central pathway issues are considered.",
  },
  peripheral_nerves: {
    key: "peripheral_nerves",
    label: "Peripheral Nerves",
    position: [0.9, -0.2, 0.06],
    lookAt: [0.52, -0.12, 0.02],
    camera: [2.1, -0.1, 2.4],
    color: "#ff9ff3",
    type: "deep",
    description: "Peripheral signal conduction outside the brain.",
    why: "Relevant when neuropathy, numbness, tingling, or peripheral nerve involvement is considered.",
  },
};

const REGION_ALIASES = {
  frontal_lobe: "frontal",
  frontal: "frontal",
  "frontal lobe": "frontal",
  "frontal cortex": "frontal",
  "prefrontal cortex": "frontal",

  temporal_lobe: "temporal",
  temporal: "temporal",
  "temporal lobe": "temporal",

  parietal_lobe: "parietal",
  parietal: "parietal",
  "parietal lobe": "parietal",

  occipital_lobe: "occipital",
  occipital: "occipital",
  "occipital lobe": "occipital",
  "occipital cortex": "occipital",

  hippocampus: "hippocampus",
  amygdala: "amygdala",

  motor: "motor",
  motor_cortex: "motor",
  "motor cortex": "motor",

  basal_ganglia: "basal_ganglia",
  "basal ganglia": "basal_ganglia",

  brainstem: "brainstem",
  "brain stem": "brainstem",
  "brainstem sleep_regulation circuits": "brainstem",
  "brainstem sleep-regulation circuits": "brainstem",

  cerebellum: "cerebellum",

  hypothalamus: "hypothalamus",

  spinal_cord: "spinal_cord",
  "spinal cord": "spinal_cord",

  peripheral_nerves: "peripheral_nerves",
  "peripheral nerves": "peripheral_nerves",
  "peripheral nerve": "peripheral_nerves",

  "trigeminal pain pathway": "brainstem",
};

function normalizeText(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/-/g, "_");
}

function normalizeRegionKey(value) {
  const text = String(value || "").toLowerCase();

  for (const [alias, key] of Object.entries(REGION_ALIASES)) {
    const cleanAlias = alias.replace(/_/g, " ").toLowerCase();

    if (
      text === cleanAlias ||
      text.includes(cleanAlias) ||
      text.includes(alias.toLowerCase())
    ) {
      return key;
    }
  }

  return normalizeText(value).replace(/\s+/g, "_");
}

function extractMappedRegions(analysis) {
  const found = new Map();

  const structuredRegions = analysis?.structured?.impacted_regions || [];
  structuredRegions.forEach((item) => {
    const regionKey = normalizeRegionKey(item?.id || item?.label);
    const config = REGION_CONFIG[regionKey];
    if (!config) return;

    found.set(regionKey, {
      key: regionKey,
      label: item?.label || config.label,
      why: item?.why || config.why,
      intensity: item?.intensity || "moderate",
      color: item?.color || config.color,
      source: "structured",
    });
  });

  const brainRegions = analysis?.brain_regions || [];
  brainRegions.forEach((item) => {
  const regionText =
    typeof item === "string"
      ? item
      : item?.region || item?.label || item?.id || "";

  const regionKey = normalizeRegionKey(regionText);
  const config = REGION_CONFIG[regionKey];
  if (!config) return;
  if (!found.has(regionKey)) {
    found.set(regionKey, {

      key: regionKey,

      label: config.label,

      why:

        typeof item === "object" && item?.why

          ? item.why

          : typeof item === "string"

          ? item

          : config.why,

      intensity:

        typeof item === "object" && item?.intensity

          ? item.intensity

          : "moderate",

      color:

        typeof item === "object" && item?.color

          ? item.color

          : config.color,

      source: "brain_regions",

    });

  }

});

  return Array.from(found.values());
}

function intensityScale(intensity) {
  const v = String(intensity || "").toLowerCase();
  if (v === "high" || v === "critical") return 1;
  if (v === "moderate" || v === "medium") return 0.72;
  if (v === "low") return 0.45;
  return 0.55;
}

function BrainModel({ dimmed, neuronMode }) {
  const { scene } = useGLTF("/brain.glb");

  const cloned = useMemo(() => {
    const copy = scene.clone(true);

    copy.traverse((child) => {
      if (child.isMesh) {
        child.castShadow = true;
        child.receiveShadow = true;

        const originalMaterial = child.material;

        child.material = new THREE.MeshStandardMaterial({
          color: "#f3d8d6",
          metalness: 0.02,
          roughness: 0.9,
          transparent: true,
          opacity: neuronMode ? 0.18 : dimmed ? 0.22 : 0.36,
          depthWrite: false,
        });

        if (originalMaterial?.map) {
          child.material.map = originalMaterial.map;
        }
      }
    });

    return copy;
  }, [scene, dimmed, neuronMode]);

  return (
    <primitive
      object={cloned}
      scale={1.35}
      position={[0, -0.2, 0]}
      rotation={[0.05, -0.35, 0]}
    />
  );
}

function CameraController({ selectedRegion, mode }) {
  const { camera } = useThree();

  useFrame(() => {
    if (mode !== "focus" || !selectedRegion) return;

    const targetCam = new THREE.Vector3(...selectedRegion.camera);
    const targetLook = new THREE.Vector3(...selectedRegion.lookAt);

    camera.position.lerp(targetCam, 0.04);
    camera.lookAt(targetLook);
  });

  return null;
}

function Marker({ config, selected, intensity, mode }) {
  const groupRef = useRef(null);

  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    const base = intensityScale(intensity);
    const pulse = selected
      ? 1 + Math.sin(t * 3.6) * 0.16
      : 1 + Math.sin(t * 2.2) * 0.08;

    if (groupRef.current) {
      groupRef.current.scale.setScalar(pulse * (0.85 + base * 0.3));
    }
  });

  const linePoints = useMemo(() => {
    const p = new THREE.Vector3(...config.position);
    const offset = new THREE.Vector3(p.x + 0.18, p.y + 0.18, p.z);
    return [p, offset];
  }, [config.position]);

  const glowOpacity = 0.12 + intensityScale(intensity) * 0.22;
  const glowScale = 2.8 + intensityScale(intensity) * 2.2;

  return (
    <>
      <group ref={groupRef} position={config.position}>
        <mesh>
          <sphereGeometry args={[selected ? 0.075 : 0.058, 28, 28]} />
          <meshStandardMaterial
            color={config.color}
            emissive={config.color}
            emissiveIntensity={selected ? 2.3 : 1.4}
            transparent
            opacity={0.98}
          />
        </mesh>

        <mesh scale={selected ? glowScale + 0.7 : glowScale}>
          <sphereGeometry args={[0.04, 24, 24]} />
          <meshStandardMaterial
            color={config.color}
            emissive={config.color}
            emissiveIntensity={2.2}
            transparent
            opacity={selected ? glowOpacity + 0.08 : glowOpacity}
            depthWrite={false}
          />
        </mesh>
      </group>

      {selected && mode === "focus" && (
        <>
          <line>
            <bufferGeometry>
              <bufferAttribute
                attach="attributes-position"
                count={linePoints.length}
                array={new Float32Array(linePoints.flatMap((v) => [v.x, v.y, v.z]))}
                itemSize={3}
              />
            </bufferGeometry>
            <lineBasicMaterial color={config.color} linewidth={2} />
          </line>

          <Html
            distanceFactor={8}
            position={[config.position[0] + 0.22, config.position[1] + 0.2, config.position[2]]}
          >
            <div className="brain-focus-label">{config.label}</div>
          </Html>
        </>
      )}
    </>
  );
}

function seededNoise(seed) {
  const x = Math.sin(seed * 999) * 10000;
  return x - Math.floor(x);
}

function buildNeuronClusters(activeRegions) {
  const clusters = [];
  const regionConnections = [];
  const neuronConnections = [];

  activeRegions.forEach((region, regionIndex) => {
    const config = REGION_CONFIG[region.key];
    if (!config) return;

    const intensity = intensityScale(region.intensity);
    const count = Math.max(18, Math.round(24 + intensity * 34));
    const regionNodeIds = [];

    for (let i = 0; i < count; i += 1) {
      const spread =
        config.type === "deep"
          ? [0.18, 0.16, 0.16]
          : [0.34, 0.26, 0.22];

      const seedBase = regionIndex * 1000 + i * 17;

      const node = {
        id: `${region.key}_${i}`,
        regionKey: region.key,
        color: region.color || config.color,
        intensity: region.intensity,
        position: [
          config.position[0] + (seededNoise(seedBase + 1) - 0.5) * spread[0],
          config.position[1] + (seededNoise(seedBase + 2) - 0.5) * spread[1],
          config.position[2] + (seededNoise(seedBase + 3) - 0.5) * spread[2],
        ],
      };

      regionNodeIds.push(node.id);
      clusters.push(node);
    }

    for (let i = 0; i < regionNodeIds.length - 1; i += 1) {
      if (i % 2 === 0 || i % 5 === 0) {
        neuronConnections.push([regionNodeIds[i], regionNodeIds[i + 1]]);
      }
    }

    if (regionIndex > 0) {
      regionConnections.push([activeRegions[regionIndex - 1].key, region.key]);
    }
  });

  const nodeMap = new Map(clusters.map((node) => [node.id, node]));

  return { nodes: clusters, regionConnections, neuronConnections, nodeMap };
}

function NeuralConnection({ from, to, color, opacity = 0.12 }) {
  const geometry = useMemo(() => {
    const start = new THREE.Vector3(...from);
    const end = new THREE.Vector3(...to);
    const mid = start.clone().lerp(end, 0.5);
    mid.y += 0.035;

    const curve = new THREE.CatmullRomCurve3([start, mid, end]);
    return new THREE.TubeGeometry(curve, 12, 0.0035, 6, false);
  }, [from, to]);

  return (
    <mesh geometry={geometry}>
      <meshBasicMaterial color={color} transparent opacity={opacity} depthWrite={false} />
    </mesh>
  );
}

function NeuronCloud({ activeRegions, selectedRegionKey, mode }) {
  const { nodes, regionConnections, neuronConnections, nodeMap } = useMemo(
    () => buildNeuronClusters(activeRegions),
    [activeRegions]
  );

  const nodeRefs = useRef([]);

  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();

    nodeRefs.current.forEach((mesh, idx) => {
      if (!mesh) return;

      const basePulse = 1 + Math.sin(t * 2.8 + idx * 0.17) * 0.22;
      mesh.scale.setScalar(basePulse);
    });
  });

  const connectionLines = useMemo(() => {
    const lines = [];

    regionConnections.forEach(([fromKey, toKey]) => {
      const fromConfig = REGION_CONFIG[fromKey];
      const toConfig = REGION_CONFIG[toKey];
      if (!fromConfig || !toConfig) return;

      lines.push({
        from: fromConfig.position,
        to: toConfig.position,
        color: toConfig.color,
      });
    });

    return lines;
  }, [regionConnections]);

  return (
    <>
      {neuronConnections.slice(0, 260).map(([fromId, toId], idx) => {
        const from = nodeMap.get(fromId);
        const to = nodeMap.get(toId);
        if (!from || !to) return null;

        return (
          <NeuralConnection
            key={`neuron_connection_${idx}`}
            from={from.position}
            to={to.position}
            color={from.color}
            opacity={mode === "network" ? 0.18 : 0.08}
          />
        );
      })}

      {connectionLines.map((line, idx) => (
        <NeuralConnection
          key={`region_connection_${idx}`}
          from={line.from}
          to={line.to}
          color={line.color}
          opacity={mode === "network" ? 0.42 : 0.22}
        />
      ))}

      {nodes.map((node, index) => {
        const highlighted =
          mode === "network" || selectedRegionKey === node.regionKey;

        const size = highlighted ? 0.035 : 0.022;

        return (
          <mesh
            key={node.id}
            ref={(el) => {
              nodeRefs.current[index] = el;
            }}
            position={node.position}
          >
            <sphereGeometry args={[size, 14, 14]} />
            <meshStandardMaterial
              color={node.color}
              emissive={node.color}
              emissiveIntensity={highlighted ? 2.5 : 1.2}
              transparent
              opacity={highlighted ? 1 : 0.82}
              depthWrite={false}
            />
          </mesh>
        );
      })}
    </>
  );
}

function IdleNeuronField({ enabled }) {
  const particles = useMemo(() => {
    if (!enabled) return [];

    return Array.from({ length: 140 }).map((_, i) => {
      const seed = i * 13;
      return {
        id: i,
        color: i % 3 === 0 ? "#38bdf8" : i % 3 === 1 ? "#a78bfa" : "#22c55e",
        position: [
          (seededNoise(seed + 1) - 0.5) * 1.8,
          (seededNoise(seed + 2) - 0.5) * 1.25 - 0.05,
          (seededNoise(seed + 3) - 0.5) * 0.8,
        ],
      };
    });
  }, [enabled]);

  const refs = useRef([]);

  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    refs.current.forEach((mesh, idx) => {
      if (!mesh) return;
      mesh.scale.setScalar(1 + Math.sin(t * 2 + idx * 0.12) * 0.18);
    });
  });

  if (!enabled) return null;

  return (
    <>
      {particles.map((p, index) => (
        <mesh
          key={p.id}
          ref={(el) => {
            refs.current[index] = el;
          }}
          position={p.position}
        >
          <sphereGeometry args={[0.012, 8, 8]} />
          <meshStandardMaterial
            color={p.color}
            emissive={p.color}
            emissiveIntensity={0.85}
            transparent
            opacity={0.38}
            depthWrite={false}
          />
        </mesh>
      ))}
    </>
  );
}

function BrainScene({ activeRegions, selectedRegionKey, mode }) {
  const selectedRegion = selectedRegionKey ? REGION_CONFIG[selectedRegionKey] : null;
  const isDeep = selectedRegion?.type === "deep";

  return (
    <Canvas camera={{ position: [0, 0.3, 3.4], fov: 42 }}>
      <ambientLight intensity={1.35} />
      <directionalLight position={[3, 3, 4]} intensity={1.15} />
      <directionalLight position={[-2, 1, -2]} intensity={0.55} />
      <pointLight position={[0, 0.4, 2]} intensity={0.7} color="#8b5cf6" />

      <Suspense fallback={null}>
        <Environment preset="city" />
        <CameraController selectedRegion={selectedRegion} mode={mode} />

        <BrainModel dimmed={isDeep} neuronMode={mode === "network"} />

        <IdleNeuronField enabled={activeRegions.length === 0} />

        <NeuronCloud
          activeRegions={activeRegions}
          selectedRegionKey={selectedRegionKey}
          mode={mode}
        />

        {activeRegions.map((region) => {
          const config = REGION_CONFIG[region.key];
          if (!config) return null;

          return (
            <Marker
              key={region.key}
              config={config}
              selected={selectedRegionKey === region.key}
              intensity={region.intensity}
              mode={mode}
            />
          );
        })}
      </Suspense>

      <OrbitControls
        enablePan={true}
        enableZoom={true}
        enableRotate={true}
        minDistance={1.4}
        maxDistance={7}
        rotateSpeed={0.75}
        zoomSpeed={0.9}
        panSpeed={0.65}
        autoRotate={mode === "network"}
        autoRotateSpeed={0.35}
      />
    </Canvas>
  );
}

export default function BrainImpactViewer({ analysis }) {
  const activeRegions = useMemo(() => extractMappedRegions(analysis), [analysis]);

  const [selectedKey, setSelectedKey] = useState(null);
  const [mode, setMode] = useState("network");

  useEffect(() => {
    if (activeRegions.length > 0) {
      setSelectedKey(activeRegions[0].key);
    } else {
      setSelectedKey(null);
    }
  }, [activeRegions]);

  const selectedRegion = selectedKey ? REGION_CONFIG[selectedKey] : null;
  const selectedRegionMeta =
    activeRegions.find((region) => region.key === selectedKey) || null;

  const rawStructuredRegions = analysis?.structured?.impacted_regions || [];
  const rawBrainRegions = analysis?.brain_regions || [];

  return (
    <div className="card">
      <div className="section-head">
        <div>
          <h2>Neuron Impact Map</h2>
          <p className="muted">
            Medgen-inferred neuron-style overlay on the brain model. This is not a scan-confirmed lesion map.
          </p>
        </div>
      </div>

      <div className="brain-map-layout">
        <div className="brain-canvas-card">
          <div className="brain-canvas-toolbar">
            <div className="brain-canvas-title-group">
              <strong>
                {mode === "network"
                  ? "Neuron Network View"
                  : selectedRegion
                  ? selectedRegion.label
                  : "No region selected"}
              </strong>
              <span className="muted">
                {mode === "network"
                  ? "Full neuron activity overlay across mapped regions"
                  : selectedRegion
                  ? selectedRegion.type === "deep"
                    ? "Deep structure view with neuron cluster"
                    : "Surface region view with neuron cluster"
                  : "Select a mapped region to focus the model"}
              </span>
            </div>

            <div className="brain-view-toggle">
              <button
                type="button"
                className={mode === "network" ? "active" : ""}
                onClick={() => setMode("network")}
              >
                Neuron View
              </button>
              <button
                type="button"
                className={mode === "focus" ? "active" : ""}
                onClick={() => setMode("focus")}
              >
                Region Focus
              </button>
            </div>
          </div>

          <div className="brain-canvas-wrap">
            <BrainScene
              activeRegions={activeRegions}
              selectedRegionKey={mode === "focus" ? selectedKey : null}
              mode={mode}
            />
          </div>
        </div>

        <div className="brain-side-panel">
          <div className="brain-side-card">
            <h3>Impacted Neuron Regions</h3>

            {activeRegions.length > 0 ? (
              <div className="brain-chip-list">
                {activeRegions.map((region) => {
                  const config = REGION_CONFIG[region.key];
                  const active = region.key === selectedKey;

                  return (
                    <button
                      key={region.key}
                      type="button"
                      className={`brain-region-chip ${active ? "active" : ""}`}
                      onClick={() => {
                        setSelectedKey(region.key);
                        setMode("focus");
                      }}
                    >
                      <span
                        className="brain-region-chip-dot"
                        style={{ background: region.color || config.color }}
                      />
                      <div className="brain-region-chip-text">
                        <strong>{config.label}</strong>
                        <p>
                          {config.type === "deep" ? "Deep structure" : "Surface region"} •{" "}
                          {String(region.intensity || "moderate")}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="empty-state-box">
                <h3>No mapped neurons yet</h3>
                <p className="muted">
                  Medgen did not return a brain-region name that matches the current neuron map vocabulary.
                </p>
              </div>
            )}
          </div>

          <div className="brain-side-card">
            <h3>Selected Region</h3>

            {selectedRegion && selectedRegionMeta ? (
              <div className="brain-selected-panel">
                <div className="brain-selected-head">
                  <span
                    className="brain-region-dot brain-region-dot-large"
                    style={{ background: selectedRegionMeta.color || selectedRegion.color }}
                  />
                  <div>
                    <strong>{selectedRegion.label}</strong>
                    <p>{selectedRegion.description}</p>
                  </div>
                </div>

                <div className="brain-selected-why">
                  <h4>Why it was mapped</h4>
                  <p>{selectedRegionMeta.why || selectedRegion.why}</p>
                </div>

                <div className="brain-selected-why">
                  <h4>Intensity</h4>
                  <p>{String(selectedRegionMeta.intensity || "moderate")}</p>
                </div>
              </div>
            ) : (
              <p className="muted">No selected region.</p>
            )}
          </div>

          <div className="brain-side-card">
            <h3>Raw Medgen region output</h3>

            {rawStructuredRegions.length > 0 || rawBrainRegions.length > 0 ? (
              <ul className="analysis-list">
                {rawStructuredRegions.map((item, index) => (
                  <li key={`structured_${index}`}>
                    {item.label || item.id} • {item.intensity || "moderate"}
                  </li>
                ))}
                {rawBrainRegions.map((item, index) => (
                  <li key={`brain_${index}`}>
                    {typeof item === "string"
                      ? item
                      : item.label || item.region || item.id}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted">No region output was returned by Medgen.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

useGLTF.preload("/brain.glb");